import {
  Layout,
  theme,
  Flex,
  Avatar,
  Badge,
  Tag,
  Select,
  Drawer,
  Button,
  Typography,
  Skeleton,
  Dropdown,
  Popover,
  List,
  Space,
  Grid,
  Tooltip,
  message,
} from 'antd';

import {
  BellOutlined,
  MenuOutlined,
  SettingOutlined,
  LogoutOutlined,
  LoadingOutlined,
  UserOutlined,
  GlobalOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
  EyeOutlined,
} from '@ant-design/icons';

import { usePlatformConfig } from 'hooks/PlatformContext';
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useLanguage } from 'hooks/Languages/useLanguage';
import { getBusinessTheme } from 'utils/themeColors';
import {
  LayoutDashboard,
  ShoppingCart,
  Package,
  Menu as MenuIcon,
  Utensils,
  ChefHat,
  X,
  Globe,
  Sliders,
  Sparkles,
  ExternalLink,
  Share2,
  Copy,
  Check,
  Zap,
} from 'lucide-react';
import fukkoniLogo from 'assets/images/fukkoni_logo.png';
import useLogout from 'hooks/useLogout';
import { useCurrency } from 'hooks/userCurrency';
import { useFetchUserData, useFetchUserPermission } from 'queries/auth/user';
import WebSocketComponent, {
  NotificationData,
} from 'services/webSocket/WebSocketComponent';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import './index.scss';
import { ImageWithFallback } from 'components/shared/ImageWithFallBack';
import { UserChangeLangDropdown } from 'components/shared/LangComponent';
import { useNavigate, useLocation } from 'react-router-dom';
import urls from 'routes/urls';
import UserProfileModal from 'components/UserProfileModal';
import MenuItems from 'utils/navigationBarItems/leftNavBarMenuItems';
import StaffNotificationsTray from 'components/shared/StaffNotificationsTray';
import { usePermissions } from 'hooks/usePermissions';
import { BranchSwitcher } from 'components/shared/BranchSwitcher';
import { getDashboardPath, getPosPath, getStockPath, getNormalizedBusinessType } from 'utils/navigation';

dayjs.extend(relativeTime);

// API base URL from environment
const mediaBaseURL = (() => {
  const url = process.env.REACT_APP_API_URL || '';
  return url.replace(/\/api\/?$/, '');
})();

const getFullUrl = (path: string) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  return `${mediaBaseURL}${path.startsWith('/') ? '' : '/'}${path}`;
};

const { Header, Content, Footer, Sider } = Layout;
const { Title, Text } = Typography;

const NotificationList: React.FC<{
  notifications: NotificationData[];
  onItemClick: (n: NotificationData) => void;
  onClearAll: () => void;
  onMarkRead: () => void;
  translate: (s: string) => string;
}> = ({ notifications, onItemClick, onClearAll, onMarkRead, translate }) => {
  return (
    <div className="notification-list-container" style={{ width: 320 }}>
      <Flex
        justify="space-between"
        align="center"
        style={{ padding: '8px 16px', borderBottom: '1px solid #f0f0f0' }}
      >
        <Title level={5} style={{ margin: 0 }}>
          {translate('Notifications')}
        </Title>
        <Space>
          <Button
            type="link"
            size="small"
            onClick={onMarkRead}
            icon={<EyeOutlined />}
          >
            {translate('Mark Read')}
          </Button>
          <Button
            type="link"
            size="small"
            danger
            onClick={onClearAll}
            icon={<DeleteOutlined />}
          >
            {translate('Clear')}
          </Button>
        </Space>
      </Flex>
      <List
        className="notification-list"
        itemLayout="horizontal"
        dataSource={notifications}
        locale={{ emptyText: translate('No new notifications') }}
        style={{ maxHeight: 400, overflowY: 'auto' }}
        renderItem={(item) => (
          <List.Item
            className={`notification-item ${item.read ? 'read' : 'unread'}`}
            onClick={() => onItemClick(item)}
            style={{
              cursor: 'pointer',
              padding: '12px 16px',
              transition: 'background 0.3s',
            }}
          >
            <List.Item.Meta
              avatar={
                <div
                  className={`notification-icon-bg ${item.type.toLowerCase()}`}
                >
                  {item.icon === 'package' && <Package size={16} />}
                  {item.icon === 'shopping-cart' && <ShoppingCart size={16} />}
                  {item.icon === 'utensils' && <Utensils size={16} />}
                  {item.icon === 'chef-hat' && <ChefHat size={16} />}
                  {!item.icon && <BellOutlined />}
                </div>
              }
              title={
                <Flex justify="space-between">
                  <Text strong={!item.read} style={{ fontSize: 13 }}>
                    {translate(item.title)}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    <ClockCircleOutlined style={{ marginRight: 4 }} />
                    {dayjs(item.timestamp).fromNow(true)}
                  </Text>
                </Flex>
              }
              description={
                <Text
                  type="secondary"
                  style={{ fontSize: 12, display: 'block' }}
                >
                  {item.message}
                </Text>
              }
            />
          </List.Item>
        )}
      />
      {notifications.length > 0 && (
        <div
          style={{
            padding: '8px',
            textAlign: 'center',
            borderTop: '1px solid #f0f0f0',
          }}
        >
          <Text type="secondary" style={{ fontSize: 12 }}>
            {translate('Showing recent alerts')}
          </Text>
        </div>
      )}
    </div>
  );
};

const RESTAURANT_BUSINESS_TYPES = ['restaurant', 'bar', 'cafe', 'coffee_shop', 'coffee shop', 'bakery', 'hotel', 'nightclub'];

const isNotificationValidForContext = (
  n: NotificationData,
  activeShopId: string,
  activeBranchId: string,
  businessType: string
) => {
  const currentBt = (businessType || 'retail').toLowerCase();

  // Multi-tenant check
  if (n.shop_id && activeShopId && String(n.shop_id) !== String(activeShopId)) return false;
  if (n.branch_id && activeBranchId && String(n.branch_id) !== String(activeBranchId)) return false;
  if (n.business_type && currentBt && String(n.business_type).toLowerCase() !== currentBt) return false;

  // Business type gating check
  const isRestaurantNotif = ['NEW_KITCHEN_ORDER', 'KITCHEN_ORDER_READY', 'WAITER_ALERT'].includes(n.type);
  const isPharmacyNotif = ['NEW_PRESCRIPTION', 'PRESCRIPTION_UPDATED', 'MEDICATION_EXPIRY_WARNING'].includes(n.type);

  if (isRestaurantNotif && !RESTAURANT_BUSINESS_TYPES.includes(currentBt)) return false;
  if (isPharmacyNotif && currentBt !== 'pharmacy') return false;

  return true;
};

const GlobalLayout: React.FC<{
  children: React.ReactNode;
  isCustomLayout?: boolean;
  notification?: number;
  hideMobileHeader?: boolean;
}> = ({ children, isCustomLayout, notification, hideMobileHeader }) => {
  const { translate } = useLanguage();
  const platformItem = usePlatformConfig();
  const screens = Grid.useBreakpoint();
  const [openDrawer, setOpenDrawer] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showStaffTray, setShowStaffTray] = useState(false);
  const [showWarningBanner, setShowWarningBanner] = useState(() => {
    return sessionStorage.getItem('dismiss_sub_warning') !== 'true';
  });

  const activeShopId = localStorage.getItem('shop_id') || localStorage.getItem('active_shop_id') || '';
  const activeBranchId = localStorage.getItem('active_branch_id') || '';
  const businessType = (localStorage.getItem('business_type') || localStorage.getItem('shop_business_type') || 'retail').toLowerCase();

  const scopedStorageKey = useMemo(() => {
    return `notifications_${activeShopId}_${activeBranchId}_${businessType}`;
  }, [activeShopId, activeBranchId, businessType]);

  // Preload core HMS page modules in background for instant navigation response
  useEffect(() => {
    const timer = setTimeout(() => {
      import('containers/hospital/queue/index').catch(() => {});
      import('containers/hospital/consultation/index').catch(() => {});
      import('containers/hospital/wards/index').catch(() => {});
      import('containers/hospital/triage/index').catch(() => {});
      import('containers/hospital/finance/index').catch(() => {});
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  const [notifications, setNotifications] = useState<NotificationData[]>(() => {
    try {
      const scoped = localStorage.getItem(scopedStorageKey);
      if (scoped) {
        return JSON.parse(scoped);
      }
      const legacy = localStorage.getItem('notifications');
      if (legacy) {
        const parsed: NotificationData[] = JSON.parse(legacy);
        return parsed.filter((n) => isNotificationValidForContext(n, activeShopId, activeBranchId, businessType));
      }
      return [];
    } catch (e) {
      return [];
    }
  });

  useEffect(() => {
    try {
      const scoped = localStorage.getItem(scopedStorageKey);
      if (scoped) {
        setNotifications(JSON.parse(scoped));
      } else {
        const legacy = localStorage.getItem('notifications');
        if (legacy) {
          const parsed: NotificationData[] = JSON.parse(legacy);
          const filtered = parsed.filter((n) => isNotificationValidForContext(n, activeShopId, activeBranchId, businessType));
          setNotifications(filtered);
        } else {
          setNotifications([]);
        }
      }
    } catch (e) {
      setNotifications([]);
    }
  }, [scopedStorageKey, activeShopId, activeBranchId, businessType]);

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.read).length,
    [notifications]
  );

  const handleNewNotification = useCallback(
    (data: NotificationData) => {
      if (!isNotificationValidForContext(data, activeShopId, activeBranchId, businessType)) {
        return;
      }
      const fullData: NotificationData = {
        ...data,
        read: false,
        shop_id: data.shop_id || activeShopId,
        branch_id: data.branch_id || activeBranchId,
        business_type: data.business_type || businessType,
      };
      setNotifications((prev) => {
        const newList = [fullData, ...prev.filter((n) => n.id !== fullData.id)];
        return newList.slice(0, 50); // Keep history of 50
      });
    },
    [activeShopId, activeBranchId, businessType]
  );

  const markAllAsRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const clearNotifications = () => {
    setNotifications([]);
  };

  const handleNotificationClick = (notif: NotificationData) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === notif.id ? { ...n, read: true } : n))
    );
    if (notif.target) {
      navigate(notif.target);
    }
  };

  const markNotificationRead = (id: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
  };

  useEffect(() => {
    const handleOpenStaffTray = () => setShowStaffTray(true);
    const handleAddTestNotif = (e: any) => {
      if (e.detail) handleNewNotification(e.detail);
    };

    window.addEventListener('openStaffTray', handleOpenStaffTray);
    window.addEventListener('fuko:add-notification', handleAddTestNotif);

    return () => {
      window.removeEventListener('openStaffTray', handleOpenStaffTray);
      window.removeEventListener('fuko:add-notification', handleAddTestNotif);
    };
  }, [handleNewNotification]);

  useEffect(() => {
    localStorage.setItem(scopedStorageKey, JSON.stringify(notifications));
    localStorage.setItem('notifications', JSON.stringify(notifications));
  }, [notifications, scopedStorageKey]);

  const { onHandleLogout, loadLogoutUser } = useLogout();
  const {
    currencies,
    loadCurrency,
    defaultCurrency,
    onSelectCurrency,
    selectedCurrency,
  } = useCurrency();
  const { userData } = useFetchUserData();
  const { hasPermission, isAdmin, roleAllowedFeatures } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (userData && !userData.is_super_admin && userData.subscription_status?.toLowerCase() === 'expired' && location.pathname !== '/renew') {
      navigate('/renew');
    }
  }, [userData, location.pathname, navigate]);

  const isSuperAdmin =
    userData?.is_super_admin ?? localStorage.getItem('isSuperAdmin') === 'true';
  const [collapsed, setCollapsed] = useState(false);
  const [hasInitializedCollapse, setHasInitializedCollapse] = useState(false);
  const [shopLogoError, setShopLogoError] = useState(false);
  const [profilePicError, setProfilePicError] = useState(false);
  const {
    token: { borderRadiusLG },
  } = theme.useToken();

  const handleOpenSideBar = () => setOpenDrawer(true);
  const handleCloseSidebar = () => setOpenDrawer(false);

  const hasSettingsPermission = hasPermission(10); // SETTINGS

  const hasPhysicalBusinessType = Boolean(
    userData?.shop?.business_type_id ||
    userData?.business_type_id ||
    userData?.user?.shop?.business_type_id ||
    userData?.user?.business_type_id
  );

  const currentPlanType = (
    userData?.shop?.plan_type ||
    userData?.plan_type ||
    localStorage.getItem('plan_type') ||
    ''
  ).toLowerCase();

  const isLiteRoute = [
    urls.merchantOnlineStore,
    urls.storefrontProducts,
    urls.storefrontOrders,
    urls.storefrontSettings,
  ].some((r) => location.pathname === r || location.pathname.startsWith(r));

  const isHotel =
    businessType === 'hotel' ||
    (typeof businessType === 'string' && (
      businessType.toLowerCase().includes('hotel') ||
      businessType.toLowerCase().includes('hospitality') ||
      businessType.toLowerCase().includes('lodge') ||
      businessType.toLowerCase().includes('resort')
    )) ||
    location.pathname.startsWith('/hotel');

  // When on any Lite route, render clean Lite dashboard without mixing Pro ERP menus
  const isLiteMerchant = !isHotel && (isLiteRoute || (currentPlanType === 'lite' && !hasPhysicalBusinessType));

  const [activeStoreSlug, setActiveStoreSlug] = useState<string>(() => {
    try {
      return localStorage.getItem('fuko_storefront_slug') || '';
    } catch {
      return '';
    }
  });

  useEffect(() => {
    const onSlugUpdated = (e: any) => {
      if (e?.detail) {
        setActiveStoreSlug(e.detail);
      }
    };
    window.addEventListener('fuko_storefront_slug_updated', onSlugUpdated);
    return () => window.removeEventListener('fuko_storefront_slug_updated', onSlugUpdated);
  }, []);

  const liveStoreUrl = useMemo(() => {
    const rawUrl = userData?.store_url || userData?.shop?.store_url;
    if (rawUrl && rawUrl.includes('store.fukkoni.com')) {
      return rawUrl;
    }
    const slug =
      activeStoreSlug ||
      userData?.storefront_slug ||
      userData?.shop?.storefront_slug ||
      userData?.shop_slug ||
      userData?.slug ||
      userData?.shop?.slug ||
      (userData?.shop_name ? userData.shop_name.toLowerCase().replace(/\s+/g, '-') : 'store');
    return `https://store.fukkoni.com/${slug}`;
  }, [userData, activeStoreSlug]);

  // ============================================================================
  // RESPONSIVE BEHAVIOR
  // ============================================================================
  const isPOSRoute = location.pathname.includes('/pos');

  // Auto-collapse sidebar based on screen width
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;

      // Tablet (768-1024): Auto-collapse to icons
      if (width <= 1024 && width > 768) {
        setCollapsed(true);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // POS Mode: Force collapsed on desktop
  useEffect(() => {
    if (isPOSRoute) {
      setCollapsed(true);
    }
  }, [isPOSRoute]);

  // Default collapse for hospital users on load
  useEffect(() => {
    if (businessType === 'hospital' && !hasInitializedCollapse) {
      setCollapsed(true);
      setHasInitializedCollapse(true);
    }
  }, [businessType, hasInitializedCollapse]);

  const mobileRoutes = useMemo(() => {
    return {
      dashboard: getDashboardPath(userData),
      sales: getPosPath(userData),
      stock: getStockPath(userData),
    };
  }, [userData]);

  // Sync sidebar state with body class for native app patterns
  useEffect(() => {
    if (openDrawer) {
      document.body.classList.add('sidebar-open');
    } else {
      document.body.classList.remove('sidebar-open');
    }
    return () => document.body.classList.remove('sidebar-open');
  }, [openDrawer]);

  // Role-based Auto-Redirection
  useEffect(() => {
    if (!userData) return;

    const isRootUrl = location.pathname === '/';
    if (!isRootUrl) return;

    // Use centralized logic for admins
    if (isAdmin) {
      navigate(getDashboardPath(userData));
      return;
    }

    // Role-based redirection for non-admin staff
    const userRole = userData?.user_role || '';
    const rolesList = userData?.user_roles || (userRole ? [userRole] : []);
    const bt = getNormalizedBusinessType(userData);

    const hasRole = (role: string) => rolesList.includes(role);
    const hasAnyRole = (roles: string[]) => roles.some(r => rolesList.includes(r));

    // Business-aware staff landing
    if (bt === 'restaurant' || bt === 'bar') {
      if (hasRole('Chef / Kitchen Staff')) {
        navigate(urls.restaurantKitchen);
      } else if (hasAnyRole(['Waitstaff / Server', 'Host / Hostess'])) {
        navigate(urls.restaurantTables);
      } else if (hasRole('Bartender')) {
        navigate(urls.barPOS);
      } else if (hasRole('Cashier')) {
        navigate(urls.barPOS);
      } else {
        navigate(urls.restaurantDashboard);
      }
    } else if (bt === 'pharmacy') {
      if (hasAnyRole(['Clinical Pharmacist', 'Pharmacist', 'Pharmacy Technician'])) {
        navigate(urls.pharmacyDispensing);
      } else if (hasRole('Cashier')) {
        navigate(urls.pharmacyPOS);
      } else {
        navigate(urls.pharmacyDashboard);
      }
    } else if (bt === 'hospital') {
      navigate(urls.hospitalQueue);
    } else if (bt === 'retail') {
      if (hasAnyRole(['Cashier', 'Sales Associate'])) {
        navigate(urls.pos);
      } else {
        navigate(urls.dashboard);
      }
    } else {
      navigate(getDashboardPath(userData));
    }
  }, [userData, location.pathname, navigate, isAdmin]);

  // Safeguard: Redirect logged-in users without any permissions/roles to the Access Pending page
  useEffect(() => {
    if (!userData) return;

    // Do not redirect if already on the PageNotFound/Access Pending page
    if (location.pathname === urls.pageNotFound) return;

    // Super Admins and Regular Admins always have access, do not redirect them
    const isUserAdmin = userData?.is_super_admin || userData?.is_admin === true || userData?.user?.user_role_id === 1;
    if (isUserAdmin) return;

    // Check if the user has any explicit permissions or role-allowed features
    const hasAnyPermissions = 
      (userData?.permissions && userData.permissions.length > 0) || 
      (roleAllowedFeatures && roleAllowedFeatures.size > 0);

    if (!hasAnyPermissions) {
      navigate(urls.pageNotFound);
    }
  }, [userData, roleAllowedFeatures, location.pathname, navigate]);

  // Safeguard: Ensure page is interactable after closing modals/drawers
  useEffect(() => {
    const handleMutation = () => {
      const checkAndCleanup = () => {
        const visibleModals = document.querySelectorAll('.ant-modal-wrap:not(.ant-modal-wrap-hidden)');
        const visibleDrawers = document.querySelectorAll('.ant-drawer-open');
        const hasOverlay = visibleModals.length > 0 || visibleDrawers.length > 0;

        if (!hasOverlay) {
          // If no overlay is visible, restore body interaction and cleanup classes
          if (document.body.style.pointerEvents === 'none') {
            document.body.style.pointerEvents = 'auto';
          }

          // Sometimes AntD forgets to remove these classes on mobile during rapid transitions
          const stuckClasses = [
            'ant-modal-open',
            'ant-drawer-open',
            'ant-scrolling-effect',
            'modal-active',
          ];
          stuckClasses.forEach((cls) => {
            if (document.body.classList.contains(cls)) {
              document.body.classList.remove(cls);
            }
          });

          // Ensure overflow is restored
          if (document.body.style.overflow === 'hidden') {
            document.body.style.overflow = '';
          }
        }
      };

      // Check immediately
      checkAndCleanup();

      // Fallback: check again after transition finishes
      setTimeout(checkAndCleanup, 350);
    };

    const observer = new MutationObserver(handleMutation);
    observer.observe(document.body, {
      attributes: true,
      childList: true,
      subtree: true,
    });

    // Initial check
    handleMutation();

    return () => observer.disconnect();
  }, []);

  const normalizedBizType = useMemo(() => {
    return getNormalizedBusinessType(userData);
  }, [userData]);

  const getSiderBg = (businessType?: string) => {
    return getBusinessTheme(normalizedBizType || businessType).sidebarBg;
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {!isLiteMerchant && (
        <Sider
          collapsible
          collapsed={collapsed}
          onCollapse={(value) => setCollapsed(value)}
          width={260}
          collapsedWidth={80}
          className={`global-left-container fixed-sidebar ${isSuperAdmin ? 'super-admin-sider' : ''} business-${(normalizedBizType || userData?.business_type || 'general').toLowerCase()}`}
          theme="dark"
        >
          <div className="logo-container">
            <span className="logo-text">
              {collapsed ? 'F' : platformItem.appName}
            </span>
          </div>

          {/* Sidebar User Info Block */}
          {userData && (
            <div className={`sidebar-user-block ${collapsed ? 'collapsed' : ''}`}>
              <Avatar
                size={collapsed ? 36 : 40}
                className="sidebar-user-avatar"
                src={(!profilePicError && userData?.picture_url) ? getFullUrl(userData.picture_url) : undefined}
                onError={() => { setProfilePicError(true); return true; }}
                style={{
                  background: 'linear-gradient(135deg, var(--primary-color-dark-5, #1e40af) 0%, var(--primary-color-light-5, #3b82f6) 100%)',
                  flexShrink: 0,
                  fontSize: 14,
                  fontWeight: 700,
                  color: '#ffffff',
                }}
              >
                {(!userData?.picture_url || profilePicError)
                  ? (userData?.first_name || userData?.username || '?')[0].toUpperCase()
                  : null}
              </Avatar>
              {!collapsed && (
                <div className="sidebar-user-info">
                  <span className="sidebar-user-name">
                    {userData?.first_name || userData?.username}
                  </span>
                  {(userData?.user_roles && userData.user_roles.length > 0) ? (
                    <span className="sidebar-user-role">
                      {userData.user_roles.join(', ')}
                    </span>
                  ) : (userData?.user_role || userData?.user_role_name) ? (
                    <span className="sidebar-user-role">
                      {userData?.user_role || userData?.user_role_name}
                    </span>
                  ) : null}
                </div>
              )}
            </div>
          )}

          {false ? ( // isLoadingPermission is gone, using userData
            <MenuSkeleton />
          ) : (
            <MenuItems
              loadLogoutUser={loadLogoutUser}
              onHandleLogout={onHandleLogout}
              userData={userData}
              collapsed={collapsed}
            />
          )}
        </Sider>
      )}

      {!isLiteMerchant && (
        <Drawer
          zIndex={3000}
          title={
            <span
              className="logo-text"
              style={{ color: '#fff', fontSize: 20, fontWeight: 700 }}
            >
              {platformItem.appName}
            </span>
          }
          closeIcon={<span style={{ color: '#fff' }}>×</span>}
          onClose={handleCloseSidebar}
          open={openDrawer}
          placement="left"
          className={`mobile-view business-${(normalizedBizType || userData?.business_type || 'general').toLowerCase()}`}
          width={280}
          styles={{
            header: {
              background: getSiderBg(userData?.business_type),
              borderBottom: '1px solid rgba(255,255,255,0.05)',
            },
            body: { background: getSiderBg(userData?.business_type), padding: 0 },
          }}
        >
          {false ? (
            <MenuSkeleton />
          ) : (
            <MenuItems
              loadLogoutUser={loadLogoutUser}
              onHandleLogout={onHandleLogout}
              userData={userData}
            />
          )}
        </Drawer>
      )}

      <Layout
        className={`main-layout ${isLiteMerchant ? 'lite-merchant-layout' : (collapsed ? 'collapsed' : 'expanded')} ${isPOSRoute ? 'pos-fullscreen-mode' : ''}`}
      >
        {!hideMobileHeader && (
          <Header
            className={`header-mobile-view global-header ${
              isLiteMerchant
                ? 'lite-header'
                : collapsed
                ? 'collapsed-sidebar'
                : 'expanded-sidebar'
            }`}
          >
            {isLiteMerchant ? (
              <div className="lite-top-bar-container">
                {/* Left section: Clean Brand & Shop Breadcrumb */}
                <div
                  className="lite-brand-breadcrumb"
                  onClick={() => navigate(urls.merchantOnlineStore)}
                >
                  <img src={fukkoniLogo} alt="FUKKONI" className="lite-brand-icon" />
                  <span className="lite-brand-title">FUKKONI</span>
                  <span className="lite-brand-badge">LITE</span>
                  <span className="lite-breadcrumb-sep">/</span>
                  <div className="lite-shop-label">
                    <span className="shop-pulse-dot" />
                    <span className="shop-name-text">{userData?.shop_name || 'My Store'}</span>
                  </div>
                </div>

                {/* Center: Clean Segmented Nav Tabs */}
                <div className="lite-nav-links">
                  <button
                    type="button"
                    className={`lite-nav-item ${location.pathname === urls.merchantOnlineStore ? 'active' : ''}`}
                    onClick={() => navigate(urls.merchantOnlineStore)}
                  >
                    <Globe size={14} />
                    <span>{translate('Bio Link')}</span>
                  </button>

                  <button
                    type="button"
                    className={`lite-nav-item ${location.pathname === urls.storefrontProducts ? 'active' : ''}`}
                    onClick={() => navigate(urls.storefrontProducts)}
                  >
                    <Package size={14} />
                    <span>{translate('Products')}</span>
                  </button>

                  <button
                    type="button"
                    className={`lite-nav-item ${location.pathname === urls.storefrontOrders ? 'active' : ''}`}
                    onClick={() => navigate(urls.storefrontOrders)}
                  >
                    <ShoppingCart size={14} />
                    <span>{translate('Orders')}</span>
                  </button>

                  <button
                    type="button"
                    className={`lite-nav-item ${location.pathname === urls.storefrontSettings ? 'active' : ''}`}
                    onClick={() => navigate(urls.storefrontSettings)}
                  >
                    <Sliders size={14} />
                    <span>{translate('Settings')}</span>
                  </button>
                </div>

                {/* Right: Store Link Badge, Language & Profile */}
                <div className="lite-right-section">
                  {liveStoreUrl && (
                    <div className="lite-store-url-chip hide-on-mobile">
                      <span className="lite-url-text">{liveStoreUrl.replace(/^https?:\/\//, '')}</span>
                      <Tooltip title={translate('Copy Store Link')}>
                        <button
                          type="button"
                          className="lite-icon-action-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigator.clipboard.writeText(liveStoreUrl);
                            message.success(translate('Store link copied!'));
                          }}
                        >
                          <Copy size={12} />
                        </button>
                      </Tooltip>
                      <a
                        href={liveStoreUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="lite-visit-btn"
                      >
                        <span>{translate('Visit')}</span>
                        <ExternalLink size={11} />
                      </a>
                    </div>
                  )}

                  {currentPlanType === 'pro' || hasPhysicalBusinessType ? (
                    <Button
                      type="primary"
                      onClick={() => navigate(getDashboardPath(userData))}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                        borderColor: '#0284c7',
                        color: '#ffffff',
                        fontWeight: 800,
                        fontSize: 12,
                        borderRadius: 10,
                        padding: '4px 14px',
                        height: 34,
                        boxShadow: '0 2px 8px rgba(2, 132, 199, 0.25)',
                      }}
                    >
                      <Zap size={14} color="#facc15" />
                      <span className="hide-on-mobile">{translate('Back to Pro ERP')}</span>
                      <span className="mobile-only-inline">{translate('Pro ERP')}</span>
                    </Button>
                  ) : (
                    <Button
                      type="default"
                      onClick={() => navigate(`${urls.merchantOnlineStore}?tab=upgrade`)}
                      className="hide-on-mobile"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        background: '#fffbeb',
                        borderColor: '#fde68a',
                        color: '#b45309',
                        fontWeight: 800,
                        fontSize: 12,
                        borderRadius: 10,
                        padding: '4px 12px',
                        height: 34,
                      }}
                    >
                      <Sparkles size={14} color="#d97706" />
                      <span>{translate('Upgrade to Pro')}</span>
                    </Button>
                  )}

                  <UserChangeLangDropdown />

                  <Dropdown
                    menu={{
                      items: [
                        {
                          key: 'profile-info',
                          label: (
                            <Flex vertical gap={4} style={{ padding: '8px 4px' }}>
                              <span style={{ fontWeight: 700, fontSize: '14px', color: '#0f172a' }}>
                                {userData?.first_name || userData?.username} {userData?.last_name}
                              </span>
                              <span style={{ fontSize: '12px', color: '#64748b' }}>{userData?.email}</span>
                              {currentPlanType === 'pro' ? (
                                <Tag color="blue" style={{ width: 'fit-content', marginTop: 4, borderRadius: 10, fontWeight: 700 }}>
                                  {translate('FUKKONI Pro Plan')}
                                </Tag>
                              ) : (
                                <Tag color="green" style={{ width: 'fit-content', marginTop: 4, borderRadius: 10, fontWeight: 700 }}>
                                  {translate('FUKKONI Lite Merchant')}
                                </Tag>
                              )}
                            </Flex>
                          ),
                          disabled: true,
                        },
                        { type: 'divider' as const },
                        {
                          key: 'view-profile',
                          icon: <UserOutlined />,
                          label: translate('Account Profile'),
                          onClick: () => setShowProfileModal(true),
                        },
                        {
                          key: 'storefront-settings',
                          icon: <SettingOutlined />,
                          label: translate('Storefront Settings'),
                          onClick: () => navigate(urls.storefrontSettings),
                        },
                        ...(currentPlanType === 'pro'
                          ? [
                              {
                                key: 'back-to-pro',
                                icon: <Zap size={14} color="#0284c7" />,
                                label: translate('Back to Pro ERP & POS'),
                                onClick: () => navigate(urls.dashboard),
                              },
                            ]
                          : [
                              {
                                key: 'upgrade-pro',
                                icon: <Sparkles size={14} color="#f59e0b" />,
                                label: translate('Upgrade to Pro POS'),
                                onClick: () => navigate(`${urls.merchantOnlineStore}?tab=upgrade`),
                              },
                            ]),
                        { type: 'divider' as const },
                        {
                          key: 'logout',
                          icon: loadLogoutUser ? <LoadingOutlined /> : <LogoutOutlined />,
                          label: translate('Logout'),
                          onClick: onHandleLogout,
                          danger: true,
                        },
                      ],
                    }}
                    trigger={['click']}
                    placement="bottomRight"
                    overlayStyle={{ width: 230 }}
                  >
                    <div className="lite-user-avatar-wrap">
                      <Avatar
                        size={36}
                        src={(!profilePicError && userData?.picture_url) ? getFullUrl(userData.picture_url) : undefined}
                        onError={() => { setProfilePicError(true); return true; }}
                        style={{
                          background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                          color: '#ffffff',
                          fontWeight: 700,
                          cursor: 'pointer',
                          boxShadow: '0 2px 8px rgba(16, 185, 129, 0.25)'
                        }}
                      >
                        {(userData?.first_name || userData?.username || 'U')[0].toUpperCase()}
                      </Avatar>
                    </div>
                  </Dropdown>
                </div>
              </div>
            ) : (
              <div className="top-bar-container">
                {/* Left section - Shop logo + name or Super Admin label */}
                <div className="top-bar-left">
                  {/* Hamburger Menu on Left for Mobile (Native feel) */}
                  <div className="mobile-only-flex mobile-hamburger-container">
                    <Button
                      type="text"
                      icon={<MenuOutlined />}
                      className="mobile-hamburger-btn"
                      onClick={handleOpenSideBar}
                    />
                  </div>

                  {/* Mobile App Branding */}
                  <div className="mobile-app-branding mobile-only-flex" style={{ alignItems: 'center', gap: '4px', marginRight: '6px' }}>
                    <span className="brand-logo-text" style={{ fontWeight: 800, fontSize: '16px', color: '#0f172a', letterSpacing: '-0.3px' }}>
                      {platformItem.appName || 'FUKKONI'}<span style={{ color: 'var(--primary-color, #0284c7)' }}>.</span>
                    </span>
                  </div>

                  {!isSuperAdmin && userData?.shop_logo && !shopLogoError && (
                    <Avatar
                      src={getFullUrl(userData.shop_logo)}
                      size={28}
                      shape="square"
                      onError={() => {
                        setShopLogoError(true);
                        return true;
                      }}
                      className="shop-logo-avatar hide-on-mobile"
                    />
                  )}

                  <Title level={5} className="shop-title hide-on-mobile">
                    {isSuperAdmin ? 'Super Admin' : userData?.shop_name || ''}
                  </Title>

                  {!isSuperAdmin && (
                    <div className="top-bar-branch-container">
                      <BranchSwitcher />
                    </div>
                  )}
                </div>

                {/* Mobile view - Unified Right Section */}
                <Flex
                  align="center"
                  gap={12}
                  className="mobile-actions-container mobile-only-flex"
                >
                  {!isSuperAdmin && (
                    <>
                      {/* Fukkoni Lite Quick Switcher */}
                      <Button
                        type="text"
                        icon={<Globe size={17} color="#059669" />}
                        onClick={() => navigate(urls.merchantOnlineStore)}
                        title={translate('Fukkoni Lite Store')}
                        style={{
                          background: '#ecfdf5',
                          borderRadius: 8,
                          width: 32,
                          height: 32,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: 0,
                          border: '1px solid #a7f3d0',
                        }}
                      />

                      {/* Notification Bell */}
                      <div className="mobile-action-item">
                        <Popover
                          placement="bottomRight"
                          trigger="click"
                          content={
                            <NotificationList
                              notifications={notifications}
                              onItemClick={handleNotificationClick}
                              onClearAll={clearNotifications}
                              onMarkRead={markAllAsRead}
                              translate={translate}
                            />
                          }
                          overlayClassName="notification-popover"
                        >
                          <Badge
                            count={unreadCount}
                            offset={[-2, 2]}
                            size="small"
                            showZero={false}
                          >
                            <Button
                              type="text"
                              icon={<BellOutlined />}
                              shape="circle"
                              className="mobile-icon-btn"
                            />
                          </Badge>
                        </Popover>
                      </div>
                    </>
                  )}

                  {/* Mobile Profile Dropdown */}
                  <div className="mobile-action-item">
                    <Dropdown
                      menu={{
                        items: [
                          {
                            key: 'profile-info',
                            label: (
                              <Flex vertical gap={4} style={{ padding: '8px 4px' }}>
                                <span
                                  style={{
                                    fontWeight: 600,
                                    fontSize: '15px',
                                    color: '#1e293b',
                                  }}
                                >
                                  {userData?.first_name || userData?.username}{' '}
                                  {userData?.last_name}
                                </span>
                                <span
                                  style={{ fontSize: '12px', color: '#64748b' }}
                                >
                                  {userData?.email}
                                </span>
                                <Flex wrap="wrap" gap={4} style={{ marginTop: 4 }}>
                                  {userData?.user_roles && userData.user_roles.length > 0 ? (
                                    userData.user_roles.map((role: string) => (
                                      <Tag
                                        key={role}
                                        color="blue"
                                        style={{
                                          width: 'fit-content',
                                          margin: 0,
                                          borderRadius: 10,
                                        }}
                                      >
                                        {translate(role)}
                                      </Tag>
                                    ))
                                  ) : (
                                    <Tag
                                      color="blue"
                                      style={{
                                        width: 'fit-content',
                                        margin: 0,
                                        borderRadius: 10,
                                      }}
                                    >
                                      {translate(userData?.user_role || '')}
                                    </Tag>
                                  )}
                                </Flex>
                              </Flex>
                            ),
                            disabled: true,
                          },
                          {
                            type: 'divider' as const,
                          },
                          ...(!isSuperAdmin
                            ? [
                                {
                                  key: 'view-profile',
                                  icon: <UserOutlined />,
                                  label: translate('View Profile'),
                                  onClick: () => setShowProfileModal(true),
                                },
                                ...(hasSettingsPermission
                                  ? [
                                      {
                                        key: 'settings',
                                        icon: <SettingOutlined />,
                                        label: translate('Settings'),
                                        onClick: () => navigate(urls.settings),
                                      },
                                    ]
                                  : []),
                                {
                                  key: 'fukkoni-lite',
                                  icon: <GlobalOutlined style={{ color: '#059669' }} />,
                                  label: translate('FUKKONI Lite Store'),
                                  onClick: () => navigate(urls.merchantOnlineStore),
                                },
                                {
                                  type: 'divider' as const,
                                },
                              ]
                            : []),
                          {
                            key: 'logout',
                            icon: loadLogoutUser ? (
                              <LoadingOutlined />
                            ) : (
                              <LogoutOutlined />
                            ),
                            label: translate('Logout'),
                            onClick: onHandleLogout,
                            danger: true,
                          },
                        ],
                      }}
                      trigger={['click']}
                      placement="bottomRight"
                      overlayStyle={{ width: 220 }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '40px', height: '40px', cursor: 'pointer' }}>
                        <Avatar
                          size={32}
                          className="mobile-avatar"
                          style={{
                            border: '1.5px solid #e2e8f0',
                            background: '#f1f5f9',
                            color: '#475569',
                            fontWeight: 600,
                            fontSize: '12px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: 0
                          }}
                          src={userData?.picture_url}
                        >
                          {userData?.first_name && userData?.last_name 
                            ? `${userData.first_name.charAt(0).toUpperCase()}${userData.last_name.charAt(0).toUpperCase()}`
                            : userData?.username?.slice(0, 2).toUpperCase() || 'U'}
                        </Avatar>
                      </div>
                    </Dropdown>
                  </div>
                </Flex>

                {/* Desktop view - Right section */}
                <div className="top-bar-right on-mobile-view">
                  {!isSuperAdmin && (
                    <Button
                      type="default"
                      onClick={() => navigate(urls.merchantOnlineStore)}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        background: '#ecfdf5',
                        borderColor: '#a7f3d0',
                        color: '#047857',
                        fontWeight: 700,
                        fontSize: 12,
                        borderRadius: 10,
                        padding: '4px 12px',
                        height: 34,
                        boxShadow: '0 1px 2px rgba(5, 150, 105, 0.06)',
                      }}
                    >
                      <Globe size={14} color="#059669" />
                      <span>{translate('Fukkoni Lite Store')}</span>
                    </Button>
                  )}

                  {!isSuperAdmin && (
                    <div className="action-group">

                      <div className="start-divider"></div>

                      {/* Notifications */}
                      <Popover
                        placement="bottomRight"
                        trigger="click"
                        content={
                          <NotificationList
                            notifications={notifications}
                            onItemClick={handleNotificationClick}
                            onClearAll={clearNotifications}
                            onMarkRead={markAllAsRead}
                            translate={translate}
                          />
                        }
                        overlayClassName="notification-popover"
                      >
                        <Badge
                          count={unreadCount}
                          offset={[-4, 4]}
                          size="small"
                          showZero={false}
                        >
                          <Button
                            type="text"
                            icon={<BellOutlined />}
                            shape="circle"
                            className="icon-btn"
                          />
                        </Badge>
                      </Popover>
                    </div>
                  )}

                  {/* Language Dropdown */}
                  <UserChangeLangDropdown />

                  <div className="start-divider"></div>

                  {/* User Profile Dropdown */}
                  <Dropdown
                    menu={{
                      items: [
                        {
                          key: 'profile-info',
                          label: (
                            <Flex vertical gap={4} style={{ padding: '8px 4px' }}>
                              <span
                                style={{
                                  fontWeight: 600,
                                  fontSize: '15px',
                                  color: '#1e293b',
                                }}
                              >
                                {userData?.first_name || userData?.username}{' '}
                                {userData?.last_name}
                              </span>
                              <span
                                style={{ fontSize: '12px', color: '#64748b' }}
                              >
                                {userData?.email}
                              </span>
                              <Flex wrap="wrap" gap={4} style={{ marginTop: 4 }}>
                                {userData?.user_roles && userData.user_roles.length > 0 ? (
                                  userData.user_roles.map((role: string) => (
                                    <Tag
                                      key={role}
                                      color="blue"
                                      style={{
                                        width: 'fit-content',
                                        margin: 0,
                                        borderRadius: 10,
                                      }}
                                    >
                                      {translate(role)}
                                    </Tag>
                                  ))
                                ) : (
                                  <Tag
                                    color="blue"
                                    style={{
                                      width: 'fit-content',
                                      margin: 0,
                                      borderRadius: 10,
                                    }}
                                  >
                                    {translate(userData?.user_role || '')}
                                  </Tag>
                                )}
                              </Flex>
                            </Flex>
                          ),
                          disabled: true,
                        },
                        {
                          type: 'divider' as const,
                        },
                        ...(!isSuperAdmin
                          ? [
                              {
                                key: 'view-profile',
                                icon: <UserOutlined />,
                                label: translate('View Profile'),
                                onClick: () => setShowProfileModal(true),
                              },
                              ...(hasSettingsPermission
                                ? [
                                    {
                                      key: 'settings',
                                      icon: <SettingOutlined />,
                                      label: translate('Settings'),
                                      onClick: () => navigate(urls.settings),
                                    },
                                  ]
                                : []),
                              {
                                key: 'fukkoni-lite',
                                icon: <GlobalOutlined style={{ color: '#059669' }} />,
                                label: translate('FUKKONI Lite Store'),
                                onClick: () => navigate(urls.merchantOnlineStore),
                              },
                              {
                                type: 'divider' as const,
                              },
                            ]
                          : []),
                        {
                          key: 'logout',
                          icon: loadLogoutUser ? (
                            <LoadingOutlined />
                          ) : (
                            <LogoutOutlined />
                          ),
                          label: translate('Logout'),
                          onClick: onHandleLogout,
                          danger: true,
                        },
                      ],
                    }}
                    trigger={['click']}
                    placement="bottomRight"
                    overlayStyle={{ width: 240 }}
                  >
                    <Button type="text" className="user-profile-pill">
                      <Flex align="center" gap="small">
                        <span className="user-name">
                          {userData?.first_name || userData?.username}
                        </span>
                        <Avatar
                          size={32}
                          className="user-avatar"
                          style={{
                            background: '#f1f5f9',
                            color: '#475569',
                            fontWeight: 600,
                            fontSize: '12px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                          src={
                            profilePicError
                              ? undefined
                              : getFullUrl(userData?.picture_url)
                          }
                          onError={() => {
                            setProfilePicError(true);
                            return true;
                          }}
                        >
                          {userData?.first_name && userData?.last_name 
                            ? `${userData.first_name.charAt(0).toUpperCase()}${userData.last_name.charAt(0).toUpperCase()}`
                            : userData?.username?.slice(0, 2).toUpperCase() || 'U'}
                        </Avatar>
                      </Flex>
                    </Button>
                  </Dropdown>
                </div>
              </div>
            )}
          </Header>
        )}
        <Content className="content-view">
          {userData?.subscription_status && ['warning', 'grace_period'].includes(userData.subscription_status.toLowerCase()) && showWarningBanner && (
            <div style={{
              position: 'sticky',
              top: !screens.md ? 48 : 64,
              zIndex: 899,
              marginTop: 0,
              marginBottom: 0,
              background: userData.subscription_status.toLowerCase() === 'grace_period' ? '#fff1f2' : '#fffbeb',
              borderBottom: `1px solid ${userData.subscription_status.toLowerCase() === 'grace_period' ? '#fecdd3' : '#fef3c7'}`,
              padding: screens.xs ? '12px 16px' : '12px 24px',
              display: 'flex',
              flexDirection: screens.xs ? 'column' : 'row',
              alignItems: screens.xs ? 'stretch' : 'center',
              justifyContent: 'space-between',
              gap: screens.xs ? 12 : 16,
              color: userData.subscription_status.toLowerCase() === 'grace_period' ? '#be123c' : '#b45309',
              fontSize: 13,
              fontWeight: 500,
              boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
            }}>
              <Flex align="start" gap={10} style={{ flex: 1 }}>
                <span style={{ fontSize: 16, marginTop: screens.xs ? 0 : 2, lineHeight: 1 }}>⚠️</span>
                <span style={{ textAlign: 'left', lineHeight: '1.4' }}>
                  {userData.subscription_status.toLowerCase() === 'grace_period' 
                    ? translate('Your subscription has expired! You are in a grace period. Please renew to avoid service suspension.') 
                    : translate('Your subscription is expiring soon! Please renew your workspace license to ensure uninterrupted service.')}
                </span>
              </Flex>
              <Flex gap={12} align="center" justify={screens.xs ? 'stretch' : 'end'} style={{ width: screens.xs ? '100%' : 'auto' }}>
                <Button 
                  type="primary" 
                  danger={userData.subscription_status.toLowerCase() === 'grace_period'} 
                  size={screens.xs ? 'middle' : 'small'} 
                  onClick={() => navigate('/renew')}
                  style={{
                    background: userData.subscription_status.toLowerCase() === 'grace_period' ? '#e11d48' : '#d97706',
                    border: 'none',
                    color: 'white',
                    fontWeight: 600,
                    height: screens.xs ? 36 : 28,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: screens.xs ? 1 : 'none',
                  }}
                >
                  {translate('Renew Now')}
                </Button>
                <Button
                  type="text"
                  icon={<X size={16} />}
                  onClick={() => {
                    setShowWarningBanner(false);
                    sessionStorage.setItem('dismiss_sub_warning', 'true');
                  }}
                  style={{
                    color: userData.subscription_status.toLowerCase() === 'grace_period' ? '#be123c' : '#b45309',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                  }}
                />
              </Flex>
            </div>
          )}
          {isCustomLayout ? (
            children
          ) : (
            <div
              style={{ borderRadius: borderRadiusLG, padding: '24px' }}
              className="dynamic-content"
            >
              {children}
            </div>
          )}
        </Content>
        <Footer
          className="large-padding"
          style={{
            textAlign: 'center',
            background: 'transparent',
            color: '#94a3b8',
          }}
        >
          FUKKONI ©{new Date().getFullYear()} Created by nk
        </Footer>
      </Layout>
      <WebSocketComponent onNewNotification={handleNewNotification} />

      <UserProfileModal
        visible={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        userData={userData}
        onRefresh={() => window.location.reload()}
      />

      {isLiteMerchant ? (
        <div className="mobile-tab-bar">
          <button
            className={`tab-item ${location.pathname === urls.merchantOnlineStore ? 'active' : ''}`}
            onClick={() => navigate(urls.merchantOnlineStore)}
          >
            <Globe size={22} className="tab-icon" />
            <span>{translate('Bio Link')}</span>
          </button>

          <button
            className={`tab-item ${location.pathname === urls.storefrontProducts ? 'active' : ''}`}
            onClick={() => navigate(urls.storefrontProducts)}
          >
            <Package size={22} className="tab-icon" />
            <span>{translate('Catalog')}</span>
          </button>

          <button
            className={`tab-item ${location.pathname === urls.storefrontOrders ? 'active' : ''}`}
            onClick={() => navigate(urls.storefrontOrders)}
          >
            <ShoppingCart size={22} className="tab-icon" />
            <span>{translate('Orders')}</span>
          </button>

          <button
            className={`tab-item ${location.pathname === urls.storefrontSettings ? 'active' : ''}`}
            onClick={() => navigate(urls.storefrontSettings)}
          >
            <Sliders size={22} className="tab-icon" />
            <span>{translate('Settings')}</span>
          </button>
        </div>
      ) : (
        !isSuperAdmin && !location.pathname.includes('/batches') && !location.pathname.startsWith('/mobile-money') && (
          <div className="mobile-tab-bar">
            <button
              className={`tab-item ${
                location.pathname === mobileRoutes.dashboard || 
                location.pathname.startsWith('/hardware/dashboard') || 
                (location.pathname === '/dashboard' && !location.pathname.includes('/hardware')) || 
                location.pathname === '/' 
                  ? 'active' 
                  : ''
              }`}
              onClick={() => navigate(mobileRoutes.dashboard)}
            >
              <LayoutDashboard size={22} className="tab-icon" />
              <span>{translate('Dashboard')}</span>
            </button>

            <button
              className={`tab-item ${
                location.pathname.startsWith(mobileRoutes.sales) || 
                location.pathname.startsWith('/pos') || 
                location.pathname.startsWith('/sales') 
                  ? 'active' 
                  : ''
              }`}
              onClick={() => navigate(mobileRoutes.sales)}
            >
              <ShoppingCart size={22} className="tab-icon" />
              <span>{translate('POS')}</span>
            </button>

            <button
              className={`tab-item ${
                location.pathname === '/hardware' ||
                location.pathname.startsWith('/hardware/') ||
                location.pathname.startsWith(mobileRoutes.stock) || 
                location.pathname.startsWith('/products') || 
                location.pathname.startsWith('/inventory') || 
                location.pathname.startsWith('/stock') ||
                location.pathname.startsWith('/tool-rentals')
                  ? 'active' 
                  : ''
              }`}
              onClick={() => navigate(mobileRoutes.stock)}
            >
              <Package size={22} className="tab-icon" />
              <span>{translate('Stock')}</span>
            </button>

            <button className="tab-item" onClick={handleOpenSideBar}>
              <MenuIcon size={22} className="tab-icon" />
              <span>{translate('More')}</span>
            </button>
          </div>
        )
      )}

      <StaffNotificationsTray
        open={showStaffTray}
        onClose={() => setShowStaffTray(false)}
        notifications={notifications}
        onMarkRead={markNotificationRead}
        onClearAll={clearNotifications}
        translate={translate}
      />
    </Layout>
  );
};

const MenuSkeleton = () => (
  <div style={{ padding: '24px 16px' }}>
    {[...Array(5)].map((_, index) => (
      <Skeleton.Button
        key={`group1-${index}`}
        active
        block
        style={{
          marginBottom: '8px',
          height: '44px',
          borderRadius: '8px',
          opacity: 0.1,
        }}
      />
    ))}
    <Skeleton.Input
      active
      block
      style={{
        marginTop: '24px',
        marginBottom: '16px',
        height: '20px',
        width: '40%',
        opacity: 0.1,
      }}
    />
    {[...Array(3)].map((_, index) => (
      <Skeleton.Button
        key={`group2-${index}`}
        active
        block
        style={{
          marginBottom: '8px',
          height: '44px',
          borderRadius: '8px',
          opacity: 0.1,
        }}
      />
    ))}
  </div>
);

export default GlobalLayout;
